import { createSelector } from 'reselect';
import uniqBy from 'lodash/uniqBy';
import sortBy from 'lodash/sortBy';
import { ALL_YEARS, ALL_TEACHERS } from '../../filter-constants';

/**
 * Redux selectors for year group data
 */

const selectYearGroups = (state) => state.yearGroups;
const selectAllClasses = (state) => state.allClasses;

const mapClasses = (classes) =>
    classes.map((c) => ({ value: c.id, label: c.name }));

const mapTeachers = (teachers) =>
    teachers.map((t) => ({ value: t.teacherProfileId, label: t.teacherName }));

const findClassIdsForTeacher = (classTeachers, teacherId) => {
    const teacher = classTeachers.find((t) => t.teacherProfileId === teacherId);
    if (teacher) {
        return teacher.classIds;
    }
    return [];
};

function filterSortTeachers(teachers) {
    teachers = [...teachers];
    teachers = uniqBy(teachers, 'value');
    teachers = sortBy(teachers, (t) => {
        const names = t.label.split(' ');
        return names[names.length - 1];
    });
    return teachers;
}

const selectAllTeachers = createSelector([selectYearGroups], (yearGroups) => {
    if (!yearGroups) {
        return [];
    }
    return filterSortTeachers(
        yearGroups.reduce(
            (prev, curr) => [...prev, ...mapTeachers(curr.classTeachers)],
            []
        )
    );
});

const selectTeachersByYear = (yearId) =>
    createSelector(
        [selectYearGroups, selectAllTeachers],
        (yearGroups, allTeachers) => {
            if (yearId === ALL_YEARS) {
                return allTeachers;
            }
            const year = yearGroups.find((y) => y.id === yearId);
            return filterSortTeachers(mapTeachers(year.classTeachers));
        }
    );

const selectClassesByYear = (yearId) =>
    createSelector(
        [selectYearGroups, selectAllClasses],
        (yearGroups, allClasses) => {
            if (yearId === ALL_YEARS) {
                return mapClasses(allClasses);
            }
            const year = yearGroups.find((y) => y.id === yearId);
            const classIds = year.classTeachers.reduce(
                (prev, curr) => [...prev, ...curr.classIds],
                []
            );
            const yearClasses = allClasses.filter(
                (c) => classIds.indexOf(c.id) !== -1
            );
            return mapClasses(yearClasses);
        }
    );

const selectClassesByYearAndTeacher = (yearId, teacherId) =>
    createSelector(
        [selectYearGroups, selectAllClasses, selectClassesByYear(yearId)],
        (yearGroups, allClasses, classesByYear) => {
            if (yearId === ALL_YEARS && teacherId === ALL_TEACHERS) {
                // all classes for all years & teachers
                return mapClasses(allClasses);
            } else if (yearId !== ALL_YEARS && teacherId === ALL_TEACHERS) {
                // classes for all teachers in a year
                return classesByYear;
            } else if (yearId === ALL_YEARS && teacherId !== ALL_TEACHERS) {
                // classes for a teacher in all years
                const classIds = yearGroups.reduce(
                    (prev, curr) => [
                        ...prev,
                        ...findClassIdsForTeacher(curr.classTeachers, teacherId)
                    ],
                    []
                );
                const teacherClasses = allClasses.filter(
                    (c) => classIds.indexOf(c.id) !== -1
                );
                return mapClasses(teacherClasses);
            } else if (yearId !== ALL_YEARS && teacherId !== ALL_TEACHERS) {
                // classes for a teacher in a year
                const year = yearGroups.find((y) => y.id === yearId);
                const teacher = year.classTeachers.find(
                    (t) => t.teacherProfileId === teacherId
                );
                if (teacher) {
                    const classIds = teacher.classIds;
                    const teacherClasses = allClasses.filter(
                        (c) => classIds.indexOf(c.id) !== -1
                    );
                    return mapClasses(teacherClasses);
                }
            }
            return [];
        }
    );

export default {
    selectYearGroups,
    selectAllTeachers,
    selectTeachersByYear,
    selectClassesByYear,
    selectClassesByYearAndTeacher
};



// WEBPACK FOOTER //
// ./components/report-filters/redux/year-groups/year-groups-selectors.js